<template>
  <div v-loading="loading" class="bulk-items-dialog p-2">
    <!-- Header summary card -->
    <div v-if="bulkInfo" class="bulk-header-card mb-3">
      <div class="bulk-header-main">
        <div class="bulk-header-title-group">
          <span class="badge bulk-type-badge" :class="operTypeBadgeClass(bulkInfo.operType)">
            <i class="bi" :class="operTypeIcon(bulkInfo.operType)"></i>
            {{ bulkInfo.operTypeName }}
          </span>
          <span class="bulk-id-chip">#{{ bulkInfo.bulkId }}</span>
        </div>
        <div class="bulk-header-status-group">
          <span class="badge bulk-status-badge" :class="statusBadgeClass(bulkInfo.requestStatus)">
            {{ bulkInfo.requestStatusName || statusName(bulkInfo.requestStatus) }}
          </span>
          <span class="bulk-date-chip">
            <i class="bi bi-clock-history me-1"></i>
            {{ bulkInfo.requestDateStr || bulkInfo.requestDate }}
          </span>
        </div>
      </div>

      <div class="bulk-header-chips">
        <div class="bulk-chip">
          <span class="bulk-chip-label">{{ $t("bulkInsuredCount") }}:</span>
          <span class="bulk-count-badge">{{ bulkInfo.totalItems || items.length }}</span>
        </div>
        <div v-if="resolveBulkProgramName" class="bulk-chip">
          <span class="bulk-chip-label">{{ $t("commonProgram") }}:</span>
          <span class="bulk-chip-val fw-bold">{{ resolveBulkProgramName }}</span>
        </div>
        <div v-if="bulkInfo.effectiveDateStr || bulkInfo.effectiveDate" class="bulk-chip">
          <span class="bulk-chip-label">{{ $t("effectiveFrom") }}:</span>
          <span class="bulk-chip-val">{{ bulkInfo.effectiveDateStr || formatDate(bulkInfo.effectiveDate) }}</span>
        </div>
        <div v-if="bulkInfo.requestUserName" class="bulk-chip">
          <span class="bulk-chip-label">{{ $t("commonCreated") }}:</span>
          <span class="bulk-chip-val">{{ bulkInfo.requestUserName }}</span>
        </div>
      </div>

      <div v-if="bulkInfo.descr" class="bulk-header-notes">
        <span class="bulk-chip-label">{{ $t("commonNotes") }}:</span>
        <span class="bulk-notes-text">{{ bulkInfo.descr }}</span>
      </div>
    </div>

    <!-- Items table -->
    <div class="table-responsive">
      <el-table
        :data="items"
        size="small"
        border
        class="el-table-css"
        max-height="460"
        style="width: 100%;"
      >
        <el-table-column fixed="left" width="50" align="center">
          <template #default="{ row }">
            <button
              type="button"
              class="table-icon-btn table-icon-btn--info"
              :title="$t('insuredChanges')"
              @click="openItemInfo(row)"
            >
              <i class="bi bi-info-circle" aria-hidden="true"></i>
            </button>
          </template>
        </el-table-column>

        <el-table-column type="index" width="45" align="center" />
        <el-table-column prop="insuredName" :label="$t('commonFullName')" min-width="190">
          <template #default="{ row }">
            <span class="fw-bold text-dark dark-text-white">
              {{ row.insuredName || [row.lastName, row.firstName, row.middleName].filter(Boolean).join(" ") }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="pin" label="PIN" width="100" align="center" />
        <el-table-column prop="cardFullNumber" :label="$t('commonCard')" width="130" align="center">
          <template #default="{ row }">
            <span>{{ row.cardFullNumber || "-" }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('bulkChangesDetails')" min-width="210">
          <template #default="{ row }">
            <!-- Program Change: Old -> New -->
            <div v-if="bulkInfo?.operType === 3" class="d-flex flex-column gap-1">
              <div class="d-inline-flex align-items-center gap-1 flex-wrap">
                <span class="bulk-diff-old" :title="resolveOldGroupName(row)">{{ resolveOldGroupName(row) }}</span>
                <i class="bi bi-arrow-right text-success"></i>
                <span class="bulk-diff-new" :title="resolveNewGroupName(row)">
                  {{ resolveNewGroupName(row) }}
                </span>
              </div>
              <div class="text-xs text-muted">
                <i class="bi bi-calendar3 me-1"></i>{{ row.startDateStr || formatDate(row.startDate) || bulkInfo?.effectiveDateStr || "-" }}
              </div>
            </div>

            <!-- Termination / Cancel -->
            <div v-else-if="bulkInfo?.operType === 2" class="d-flex flex-column gap-1">
              <span class="badge badge--danger w-fit">
                <i class="bi bi-x-circle me-1"></i>{{ $t("insuredCancelDate") }}: {{ row.endDateStr || formatDate(row.endDate) || bulkInfo?.effectiveDateStr || "-" }}
              </span>
            </div>

            <!-- Data Change / Məlumat dəyişikliyi -->
            <div v-else-if="bulkInfo?.operType === 4" class="d-flex flex-column gap-1">
              <span class="badge badge--purple w-fit">
                <i class="bi bi-pencil-square me-1"></i>{{ $t("insuredUpdateInfo") || "Məlumat dəyişikliyi" }}
              </span>
              <div v-if="row.phone" class="text-xs text-muted">
                <i class="bi bi-telephone me-1"></i>{{ row.phone }}
              </div>
              <div v-if="row.address" class="text-xs text-muted text-truncate" style="max-width: 250px;">
                <i class="bi bi-geo-alt me-1"></i>{{ row.address }}
              </div>
            </div>

            <!-- New Insured / Import -->
            <div v-else class="d-flex flex-column gap-1">
              <span class="badge badge--info w-fit">
                <i class="bi bi-person-plus me-1"></i>{{ resolveNewGroupName(row) }}
              </span>
              <div v-if="row.startDateStr || row.startDate" class="text-xs text-muted">
                <i class="bi bi-calendar3 me-1"></i>{{ row.startDateStr || formatDate(row.startDate) }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('commonStatus')" width="105" align="center">
          <template #default="{ row }">
            <span class="badge" :class="row.isEmployee ? 'badge--info' : 'badge--muted'">
              {{ row.isEmployee ? $t("commonEmployee") : $t("commonFamilyMember") }}
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('requestDecisionNote')" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="badge" :class="statusBadgeClass(row.itemStatus)">
              {{ row.statusNote || statusName(row.itemStatus) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top flex-wrap gap-2">
      <button
        v-if="bulkInfo && bulkInfo.bulkId"
        type="button"
        class="action-btn action-btn--danger action-btn--compact mb-0"
        @click="handleDeleteBulk"
      >
        <i class="bi bi-trash3 me-1"></i>
        <span>{{ $t("commonDelete") || "Müraciəti sil" }}</span>
      </button>
      <button type="button" class="action-btn action-btn--muted action-btn--compact mb-0 ms-auto" @click="$emit('close')">
        {{ $t("commonClose") }}
      </button>
    </div>

    <!-- Info comparison modal for individual item -->
    <el-dialog
      v-if="infoDialogVisible"
      v-model="infoDialogVisible"
      :title="$t('insuredChanges')"
      width="640px"
      top="8vh"
      append-to-body
      destroy-on-close
    >
      <ChangeInfoDialog :info="selectedItemInfo" />
    </el-dialog>
  </div>
</template>

<script>
import Source from "@/project/Source/claim.js";
import ChangeInfoDialog from "@/project/Global/Modals/ChangeInfoDialog.vue";
import { swalDialogClass } from "@/project/Source/helper.js";

export default {
  name: "ReqBulkItemsDialog",
  components: {
    ChangeInfoDialog,
  },
  props: {
    bulkInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["close", "deleted"],
  data() {
    return {
      items: [],
      loading: false,
      groups: [],
      infoDialogVisible: false,
      selectedItemInfo: {},
    };
  },
  computed: {
    resolveBulkProgramName() {
      if (this.bulkInfo?.groupName && this.bulkInfo.groupName !== "-") {
        return this.bulkInfo.groupName;
      }
      if (this.bulkInfo?.groupId) {
        const g = this.groups.find((x) => Number(x.groupId || x.id) === Number(this.bulkInfo.groupId));
        if (g) return g.groupName || g.name;
      }
      return null;
    },
  },
  watch: {
    bulkInfo: {
      immediate: true,
      handler(val) {
        if (val && val.bulkId) {
          this.loadItems(val.bulkId);
          if (val.policyId) {
            Source.Groups({
              id: Number(val.policyId),
              requestCorrelationId: "string",
            })
              .then((res) => {
                this.groups = res.data?.data || [];
              })
              .catch(() => {
                this.groups = [];
              });
          }
        } else {
          this.items = [];
          this.groups = [];
        }
      },
    },
  },
  methods: {
    operTypeIcon(type) {
      if (type === 1) return "bi-person-plus";
      if (type === 2) return "bi-person-x";
      if (type === 3) return "bi-arrow-left-right";
      if (type === 4) return "bi-pencil-square";
      return "bi-folder";
    },
    resolveNewGroupName(row) {
      if (row.newGroupName && row.newGroupName !== "-") return row.newGroupName;
      if (this.bulkInfo?.groupName && this.bulkInfo.groupName !== "-") return this.bulkInfo.groupName;
      const targetGroupId = row.newGroupId || this.bulkInfo?.groupId;
      if (targetGroupId) {
        const g = this.groups.find((x) => Number(x.groupId || x.id) === Number(targetGroupId));
        if (g) return g.groupName || g.name;
      }
      return "-";
    },
    resolveOldGroupName(row) {
      if (row.oldGroupName && row.oldGroupName !== "-") return row.oldGroupName;
      if (row.oldGroupId) {
        const g = this.groups.find((x) => Number(x.groupId || x.id) === Number(row.oldGroupId));
        if (g) return g.groupName || g.name;
      }
      return "-";
    },
    openItemInfo(row) {
      this.selectedItemInfo = {
        pin: row.pin,
        oldPin: row.oldPin,
        firstName: row.firstName,
        oldFirstName: row.oldFirstName,
        lastName: row.lastName,
        oldLastName: row.oldLastName,
        middleName: row.middleName,
        oldMiddleName: row.oldMiddleName,
        sex: row.sex,
        oldSex: row.oldSex,
        dateOfBirthStr: row.dobStr || this.formatDate(row.dob),
        oldDateOfBirthStr: row.oldDobStr || this.formatDate(row.oldDob),
        phoneMobile: row.phone,
        oldPhone: row.oldPhone,
        address: row.address,
        oldAddress: row.oldAddress,
        employeeStatusName: row.isEmployee === true ? this.$t("commonEmployee") : (row.isEmployee === false ? this.$t("commonFamilyMember") : null),
        oldEmployeeStatusName: row.oldIsEmployee === true ? this.$t("commonEmployee") : (row.oldIsEmployee === false ? this.$t("commonFamilyMember") : null),
        groupName: this.resolveNewGroupName(row) !== "-" ? this.resolveNewGroupName(row) : null,
        oldGroupName: this.resolveOldGroupName(row) !== "-" ? this.resolveOldGroupName(row) : null,
        startDateStr: row.startDateStr || this.formatDate(row.startDate) || this.bulkInfo?.effectiveDateStr || this.formatDate(this.bulkInfo?.effectiveDate),
        oldStartDateStr: row.oldStartDateStr || this.formatDate(row.oldStartDate),
        endDateStr: row.endDateStr || this.formatDate(row.endDate) || (this.bulkInfo?.operType === 2 ? (this.bulkInfo?.effectiveDateStr || this.formatDate(this.bulkInfo?.effectiveDate)) : null),
        oldEndDateStr: row.oldEndDateStr || this.formatDate(row.oldEndDate),
        descr: row.descr || this.bulkInfo?.descr,
        statusNote: row.statusNote || this.bulkInfo?.statusNote,
      };
      this.infoDialogVisible = true;
    },
    loadItems(bulkId) {
      this.loading = true;
      Source.AccountRequestBulkItems({
        id: bulkId,
        requestCorrelationId: "string",
      })
        .then((res) => {
          this.items = res.data?.data || [];
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(dateVal) {
      if (!dateVal) return "";
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return "";
      return ("0" + d.getDate()).slice(-2) + "." + ("0" + (d.getMonth() + 1)).slice(-2) + "." + d.getFullYear();
    },
    operTypeBadgeClass(type) {
      if (type === 1) return "badge--info";
      if (type === 2) return "badge--danger";
      if (type === 3) return "badge--warning";
      if (type === 4) return "badge--purple";
      return "badge--muted";
    },
    statusBadgeClass(status) {
      if (status === 0) return "badge--warning";
      if (status === 1) return "badge--success";
      if (status === 2) return "badge--danger";
      return "badge--muted";
    },
    statusName(status) {
      if (status === 0) return this.$t("commonNew");
      if (status === 1) return this.$t("commonApproved");
      if (status === 2) return this.$t("commonRejected");
      return "";
    },
    handleDeleteBulk() {
      if (!this.bulkInfo || !this.bulkInfo.bulkId) return;
      const operName = this.bulkInfo.operTypeName || "";
      this.$swal({
        title: this.$t("commonWarning") || "Diqqət",
        text: `#${this.bulkInfo.bulkId} nömrəli "${operName}" müraciətini silmək istədiyinizdən əminsiniz? Bütün (${this.items.length || this.bulkInfo.totalItems}) qeydlər silinəcək.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("commonDelete") || "Bəli, sil",
        cancelButtonText: this.$t("commonCancel") || "İmtina",
        customClass: swalDialogClass(),
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          Source.AccountRequestBulkDelete({ id: this.bulkInfo.bulkId, requestCorrelationId: "string" })
            .then((res) => {
              if (res.data?.isSuccess) {
                this.$swal({
                  title: this.$t("commonSuccess") || "Uğurlu",
                  text: `#${this.bulkInfo.bulkId} nömrəli müraciət uğurla silindi.`,
                  icon: "success",
                  timer: 1800,
                  showConfirmButton: false,
                  customClass: swalDialogClass(),
                });
                this.$emit("deleted");
                this.$emit("close");
              } else {
                this.$swal({
                  title: this.$t("commonError") || "Xəta",
                  text: (res.data?.errors || []).join(", ") || "Müraciəti silmək mümkün olmadı.",
                  icon: "error",
                  customClass: swalDialogClass(),
                });
              }
            })
            .catch((err) => {
              this.$swal({
                title: this.$t("commonError") || "Xəta",
                text: err?.message || "Müraciəti silmək mümkün olmadı.",
                icon: "error",
                customClass: swalDialogClass(),
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.bulk-header-card {
  background: var(--app-surface, #ffffff);
  border: 1px solid var(--app-border, #e2e8f0);
  border-radius: var(--app-radius, 12px);
  padding: 1rem 1.25rem;
  box-shadow: var(--app-shadow-card, 0 2px 8px rgba(0, 0, 0, 0.04));
}

.bulk-header-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--app-border, #e2e8f0);
}

.bulk-header-title-group,
.bulk-header-status-group {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.bulk-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
}

.bulk-id-chip {
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--app-heading, #0f172a);
}

.bulk-status-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
}

.bulk-date-chip {
  font-size: 0.75rem;
  color: var(--app-text-muted, #64748b);
  display: inline-flex;
  align-items: center;
}

.bulk-header-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
  margin-top: 0.85rem;
}

.bulk-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.bulk-chip-label {
  font-weight: 700;
  color: var(--app-heading, #0f172a);
}

.bulk-chip-val {
  font-weight: 600;
  color: var(--app-text, #334155);
}

.bulk-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1;
  min-width: 24px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #0284c7;
  color: #ffffff !important;
  box-shadow: 0 2px 4px rgba(2, 132, 199, 0.35);
}

.bulk-header-notes {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed var(--app-border, #e2e8f0);
  font-size: 0.8125rem;
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
}

.bulk-notes-text {
  color: var(--app-text, #475569);
  font-style: italic;
}

.badge--info {
  background: color-mix(in srgb, #0284c7 20%, transparent);
  color: #0284c7;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
}
.badge--warning {
  background: color-mix(in srgb, #d97706 20%, transparent);
  color: #d97706;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
}
.badge--purple {
  background: color-mix(in srgb, #8b5cf6 20%, transparent);
  color: #8b5cf6;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
}
.badge--success {
  background: color-mix(in srgb, #16a34a 20%, transparent);
  color: #16a34a;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
}
.badge--danger {
  background: color-mix(in srgb, #dc2626 20%, transparent);
  color: #dc2626;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
}
.badge--muted {
  background: color-mix(in srgb, #64748b 20%, transparent);
  color: #64748b;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
}
.w-fit {
  width: fit-content;
}
.bulk-diff-old {
  text-decoration: line-through;
  background: var(--app-surface-muted, #f1f5f9);
  color: var(--app-text-muted, #64748b);
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.78rem;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bulk-diff-new {
  font-weight: 600;
  color: #16a34a;
  background: rgba(34, 197, 94, 0.15);
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.78rem;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
